import { useEffect, useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { Activity, Building2, CreditCard, History, KeyRound, Layers, Loader2, LockKeyhole, Megaphone, MessageSquareText, Pencil, Users, UsersRound } from "lucide-react";
import { toast } from "sonner";
import { errorMessage, get, post } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useAsync } from "@/lib/queries";
import { formatDate, TEAM_ROLE_LABELS } from "@/lib/constants";
import { EmptyState, ErrorState, LoadingRows, PageHeader } from "@/components/States";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { money } from "@/lib/plans";
import { CompanyDialog } from "./CompanyDialog";
import { SupportLog } from "./SupportLog";
import { PlanManagementSection } from "./Plans";
import MortiseLocks from "@/pages/MortiseLocks";
import Billing from "./Billing";
import { DashboardAlertAdmin } from "./DashboardAlertAdmin";
import { SmsSettingsAdmin } from "./SmsSettingsAdmin";
import { UserTypesAdmin } from "./UserTypesAdmin";
import { SquareAdmin } from "./SquareAdmin";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

function statusOf(c) {
  if (c.suspended) return ["Suspended", "bg-rose-50 text-rose-700"];
  if (c.complimentary) return ["Complimentary", "bg-emerald-50 text-emerald-700"];
  if (["active", "trialing"].includes(c.subscription_status)) return ["Subscribed", "bg-emerald-50 text-emerald-700"];
  if (c.subscription_status === "past_due") return ["Payment overdue", "bg-amber-50 text-amber-800"];
  if (c.active) return ["Trial", "bg-blue-50 text-blue-800"];
  return [c.subscription_status === "canceled" ? "Cancelled" : "Trial ended", "bg-zinc-100 text-zinc-600"];
}

const PlanPicker = ({ c, plans, own, onAssign }) => {
  const current = plans.find((p) => p.id === c.plan_id);
  if (own) return <span className="text-sm text-slate-600">Complimentary</span>;
  const value = c.complimentary ? "complimentary" : c.plan_id || "";
  const options = plans.filter((p) => !p.archived || p.id === c.plan_id);
  return (
    <div>
      <Select value={value} onValueChange={(v) => onAssign(c, v)}>
        <SelectTrigger className="h-8 w-56 text-sm" data-testid={`company-plan-select-${c.id}`}><SelectValue placeholder="No plan" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="complimentary" data-testid="company-plan-option-complimentary">Complimentary</SelectItem>
          {options.map((p) => <SelectItem key={p.id} value={p.id} disabled={p.archived} data-testid={`company-plan-option-${p.id}`}>{p.name} ({p.price ? money(p.price) : "no price"}{p.display_only ? ", show only" : ""})</SelectItem>)}
        </SelectContent>
      </Select>
      {!c.complimentary && c.subscribed_amount != null && current && c.subscribed_amount !== current.price &&
        <p className="mt-1 text-xs text-slate-500" data-testid={`company-locked-price-${c.id}`}>Pays {money(c.subscribed_amount)} / month</p>}
    </div>
  );
};

const Row = ({ c, onAction, own, plans, onAssign, onOpen }) => {
  const [label, tone] = statusOf(c);
  return (
    <TableRow data-testid={`company-row-${c.id}`}>
      <TableCell><button type="button" onClick={() => onOpen(c)} className="text-left font-semibold text-blue-700 hover:underline" data-testid={`company-open-${c.id}`}>{c.name}</button></TableCell>
      <TableCell className="font-data text-sm">{c.created_at ? formatDate(c.created_at.slice(0, 10), "dd/MM/yyyy") : "—"}</TableCell>
      <TableCell className="text-sm">{c.managers.join(", ") || "—"}</TableCell>
      <TableCell className="font-data text-sm">{c.locksmith_count}</TableCell>
      <TableCell className="font-data text-sm">{c.run_count}</TableCell>
      <TableCell><PlanPicker c={c} plans={plans} own={own} onAssign={onAssign} /></TableCell>
      <TableCell>
        <span className={`whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-semibold ${tone}`} data-testid={`company-status-${c.id}`}>{label}</span>
        {["active", "trialing", "past_due", "canceled"].includes(c.subscription_status) && (
          <span className="mt-1 block whitespace-nowrap text-[11px] text-slate-500" data-testid={`company-provider-${c.id}`}>
            {c.payment_provider === "square" ? "Square" : "Stripe"}{c.current_period_end ? ` · ${c.subscription_status === "canceled" ? "ended" : "next"} ${formatDate(c.current_period_end.slice(0, 10), "dd/MM/yyyy")}` : ""}
          </span>
        )}
      </TableCell>
      <TableCell className="text-right">
        {own ? <span className="text-xs text-slate-500" data-testid={`company-own-${c.id}`}>Your company</span> : (
        <Button size="sm" variant="outline" onClick={() => onAction(c)} data-testid={`company-${c.suspended ? "reactivate" : "suspend"}-${c.id}`}
          className={c.suspended ? "text-emerald-700" : "text-rose-700"}>{c.suspended ? "Reactivate" : "Suspend"}</Button>)}
      </TableCell>
    </TableRow>
  );
};

const MEMBERS_PAGE_SIZE = 200;
const ACTIONS_PAGE_SIZE = 100;

const SitewideActions = () => {
  const [actions, setActions] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = async (offset = 0) => {
    setLoading(true);
    setError("");
    try {
      const page = await get("/platform/sitewide-actions", { skip: offset, limit: ACTIONS_PAGE_SIZE });
      setActions((current) => offset ? [...current, ...page.actions] : page.actions);
      setTotal(page.total);
      setSkip(offset + page.actions.length);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <section className="space-y-4" data-testid="sitewide-actions">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <h2 className="font-display text-lg font-bold text-slate-900">Sitewide Actions</h2>
          <p className="mt-1 text-sm text-slate-500">Run, team, and Admin changes across all companies.</p>
        </div>
        {!loading && <p className="text-sm text-slate-500" data-testid="sitewide-actions-count">{actions.length} of {total}</p>}
      </div>
      {error && <ErrorState message={error} onRetry={() => load()} testId="sitewide-actions-error" />}
      {loading && !actions.length && <LoadingRows rows={5} testId="sitewide-actions-loading" />}
      {!loading && !error && !actions.length && <EmptyState icon={Activity} title="No actions yet" testId="sitewide-actions-empty" />}
      {actions.length > 0 && (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
          <Table data-testid="sitewide-actions-table">
            <TableHeader><TableRow className="bg-slate-50">
              <TableHead>When</TableHead><TableHead>Who</TableHead><TableHead>Company</TableHead><TableHead>Action</TableHead><TableHead>Record</TableHead>
            </TableRow></TableHeader>
            <TableBody>{actions.map((action) => (
              <TableRow key={action.id} data-testid={`sitewide-action-${action.id}`}>
                <TableCell className="whitespace-nowrap text-sm">{new Date(action.at).toLocaleString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}</TableCell>
                <TableCell className="text-sm">{action.actor_name}</TableCell>
                <TableCell className="text-sm">{action.company_name}</TableCell>
                <TableCell className="text-sm font-medium">{action.summary}</TableCell>
                <TableCell className="max-w-xs truncate text-sm text-slate-600">{action.target || "—"}</TableCell>
              </TableRow>
            ))}</TableBody>
          </Table>
        </div>
      )}
      {skip < total && <Button variant="outline" onClick={() => load(skip)} disabled={loading} data-testid="sitewide-actions-load-more">
        {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Load more actions
      </Button>}
    </section>
  );
};

const SitewideMembers = () => {
  const [members, setMembers] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [addLocksmithTypeOpen, setAddLocksmithTypeOpen] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = async (offset = 0) => {
    setLoading(true);
    setError("");
    try {
      const page = await get("/platform/members", { skip: offset, limit: MEMBERS_PAGE_SIZE });
      setMembers((current) => offset ? [...current, ...page.members] : page.members);
      setTotal(page.total);
      setSkip(offset + page.members.length);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  const addLocksmithType = async () => {
    setBulkBusy(true);
    try {
      const result = await post("/platform/members/add-locksmith-type");
      toast.success(`Added Locksmith user type to ${result.updated_count} membership${result.updated_count === 1 ? "" : "s"}`);
      await load();
    } catch (e) {
      toast.error(errorMessage(e));
      return false;
    } finally {
      setBulkBusy(false);
    }
    return undefined;
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <section className="space-y-4" data-testid="sitewide-members">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="font-display text-lg font-bold text-slate-900">Sitewide members</h2>
          <p className="mt-1 text-sm text-slate-500">One row per company membership, including pending invitations.</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {!loading && <p className="text-sm text-slate-500" data-testid="sitewide-members-count">{members.length} of {total}</p>}
          <Button variant="outline" onClick={() => setAddLocksmithTypeOpen(true)} disabled={bulkBusy} data-testid="sitewide-add-locksmith-type-button">
            <KeyRound className="mr-1 h-4 w-4" />Add Locksmith user type
          </Button>
        </div>
      </div>
      {error && <ErrorState message={error} onRetry={() => load()} testId="sitewide-members-error" />}
      {loading && !members.length && <LoadingRows rows={5} testId="sitewide-members-loading" />}
      {!loading && !error && !members.length && <EmptyState icon={UsersRound} title="No members yet" testId="sitewide-members-empty" />}
      {members.length > 0 && (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
          <Table data-testid="sitewide-members-table">
            <TableHeader><TableRow className="bg-slate-50">
              <TableHead>Member</TableHead><TableHead>Email</TableHead><TableHead>Role</TableHead><TableHead>Company</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>{members.map((member) => (
              <TableRow key={`${member.company_id}-${member.id}`} data-testid={`sitewide-member-${member.company_id}-${member.id}`}>
                <TableCell className="font-semibold text-slate-900">{member.full_name}</TableCell>
                <TableCell className="text-sm">{member.email}</TableCell>
                <TableCell className="text-sm">{TEAM_ROLE_LABELS[member.role] || member.role}</TableCell>
                <TableCell className="text-sm">{member.company_name}</TableCell>
                <TableCell><span className={`whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-semibold ${member.membership_status === "invited" ? "bg-amber-50 text-amber-800" : member.active ? "bg-emerald-50 text-emerald-700" : "bg-zinc-100 text-zinc-600"}`}>
                  {member.membership_status === "invited" ? "Invited" : member.active ? "Active" : "Inactive"}
                </span></TableCell>
                <TableCell className="text-right"><Button asChild size="icon" variant="ghost" aria-label={`Edit ${member.full_name}`} title="Edit member" data-testid={`sitewide-member-edit-${member.company_id}-${member.id}`}>
                  <Link to={`/manager/admin/members/${member.company_id}/${member.id}`} state={{ returnTo: "/manager/admin?tab=members" }}><Pencil className="h-4 w-4" /></Link>
                </Button></TableCell>
              </TableRow>
            ))}</TableBody>
          </Table>
        </div>
      )}
      {skip < total && <Button variant="outline" onClick={() => load(skip)} disabled={loading} data-testid="sitewide-members-load-more">
        {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Load more members
      </Button>}
      <ConfirmDialog open={addLocksmithTypeOpen} onOpenChange={setAddLocksmithTypeOpen} onConfirm={addLocksmithType}
        title="Add Locksmith type to all non-managers?" confirmLabel="Update members"
        description="Adds the Locksmith user type to active and invited memberships across every company. Existing user types are preserved. Manager memberships are not changed. You can run this again safely." />
    </section>
  );
};

export default function Companies() {
  const { profile } = useAuth();
  const [searchParams] = useSearchParams();
  const { data, loading, error, reload } = useAsync(() => (profile.is_platform_owner ? get("/platform/companies") : Promise.resolve([])), []);
  const plans = useAsync(() => (profile.is_platform_owner ? get("/platform/plans") : Promise.resolve([])), []);
  const [target, setTarget] = useState(null);
  const [compTarget, setCompTarget] = useState(null);
  const [openId, setOpenId] = useState(null);
  if (!profile.is_platform_owner) return <Navigate to="/manager" replace />;

  const assign = async (c, v, confirmed) => {
    if (v === "complimentary" && !confirmed && ["active", "trialing", "past_due"].includes(c.subscription_status)) {
      setCompTarget(c);
      return undefined;
    }
    try {
      await post(`/platform/companies/${c.id}/plan`, v === "complimentary" ? { complimentary: true } : { plan_id: v });
      toast.success(`${c.name} updated`);
      reload();
      plans.reload();
    } catch (e) {
      toast.error(errorMessage(e));
      return false;
    }
    return undefined;
  };

  const act = async () => {
    try {
      await post(`/platform/companies/${target.id}/${target.suspended ? "reactivate" : "suspend"}`);
      toast.success(target.suspended ? "Company reactivated" : "Company suspended");
      reload();
    } catch (e) {
      toast.error(errorMessage(e));
      return false;
    }
  };

  return (
    <div className="space-y-8" data-testid="companies-page" data-page="owner-account">
      <PageHeader eyebrow="Platform" title="Admin" description="Manage companies, members, subscriptions, plans and support visits across Warrant Whizz." />
      {error && <ErrorState message={error} onRetry={reload} />}
      {loading && !data && <LoadingRows />}
      <Tabs defaultValue={searchParams.get("tab") || "companies"} className="space-y-4" data-testid="owner-account-tabs">
        <TabsList className="h-auto w-full flex-wrap justify-start gap-1.5 rounded-xl border border-slate-200 bg-slate-100/80 p-1.5" aria-label="Admin sections">
          <TabsTrigger value="companies" className="gap-2 text-slate-600 hover:text-slate-900 data-[state=active]:bg-white data-[state=active]:text-blue-900 data-[state=active]:shadow-sm" data-testid="owner-account-tab-companies"><Building2 className="h-4 w-4" />Companies</TabsTrigger>
          <TabsTrigger value="members" className="gap-2 text-slate-600 hover:text-slate-900 data-[state=active]:bg-white data-[state=active]:text-blue-900 data-[state=active]:shadow-sm" data-testid="owner-account-tab-members"><UsersRound className="h-4 w-4" />Members</TabsTrigger>
          <TabsTrigger value="plans" className="gap-2 text-slate-600 hover:text-slate-900 data-[state=active]:bg-white data-[state=active]:text-blue-900 data-[state=active]:shadow-sm" data-testid="owner-account-tab-plans"><Layers className="h-4 w-4" />Plans</TabsTrigger>
          <TabsTrigger value="support" className="gap-2 text-slate-600 hover:text-slate-900 data-[state=active]:bg-white data-[state=active]:text-blue-900 data-[state=active]:shadow-sm" data-testid="owner-account-tab-support"><History className="h-4 w-4" />Support visits</TabsTrigger>
          <TabsTrigger value="actions" className="gap-2 text-slate-600 hover:text-slate-900 data-[state=active]:bg-white data-[state=active]:text-blue-900 data-[state=active]:shadow-sm" data-testid="owner-account-tab-actions"><Activity className="h-4 w-4" />Sitewide Actions</TabsTrigger>
          <TabsTrigger value="dashboard-alert" className="gap-2 text-slate-600 hover:text-slate-900 data-[state=active]:bg-white data-[state=active]:text-blue-900 data-[state=active]:shadow-sm" data-testid="owner-account-tab-dashboard-alert"><Megaphone className="h-4 w-4" />Dashboard alert</TabsTrigger>
          <TabsTrigger value="texts" className="gap-2 text-slate-600 hover:text-slate-900 data-[state=active]:bg-white data-[state=active]:text-blue-900 data-[state=active]:shadow-sm" data-testid="owner-account-tab-texts"><MessageSquareText className="h-4 w-4" />Texts</TabsTrigger>
          <TabsTrigger value="user-types" className="gap-2 text-slate-600 hover:text-slate-900 data-[state=active]:bg-white data-[state=active]:text-blue-900 data-[state=active]:shadow-sm" data-testid="owner-account-tab-user-types"><Users className="h-4 w-4" />Account types</TabsTrigger>
          <TabsTrigger value="square" className="gap-2 text-slate-600 hover:text-slate-900 data-[state=active]:bg-white data-[state=active]:text-blue-900 data-[state=active]:shadow-sm" data-testid="owner-account-tab-square"><CreditCard className="h-4 w-4" />Square</TabsTrigger>
          <TabsTrigger value="mortise-locks" className="gap-2 text-slate-600 hover:text-slate-900 data-[state=active]:bg-white data-[state=active]:text-blue-900 data-[state=active]:shadow-sm" data-testid="owner-account-tab-mortise-locks"><LockKeyhole className="h-4 w-4" />Mortise Locks</TabsTrigger>
          <TabsTrigger value="billing" className="gap-2 text-slate-600 hover:text-slate-900 data-[state=active]:bg-white data-[state=active]:text-blue-900 data-[state=active]:shadow-sm" data-testid="owner-account-tab-billing"><CreditCard className="h-4 w-4" />Billing</TabsTrigger>
        </TabsList>
        <TabsContent value="companies" className="mt-0 space-y-4" data-testid="owner-account-panel-companies">
          {data && !data.length && <EmptyState icon={Building2} title="No companies yet" testId="companies-empty" />}
          {data?.length > 0 && (
            <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
              <Table data-testid="companies-table">
                <TableHeader><TableRow className="bg-slate-50">
                  <TableHead>Company</TableHead><TableHead>Signed up</TableHead><TableHead>Managers</TableHead><TableHead>Locksmiths</TableHead><TableHead>Runs</TableHead><TableHead>Plan</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead>
                </TableRow></TableHeader>
                <TableBody>{data.map((c) => <Row key={c.id} c={c} onAction={setTarget} own={c.id === profile.company_id} plans={plans.data || []} onAssign={assign} onOpen={(x) => setOpenId(x.id)} />)}</TableBody>
              </Table>
            </div>
          )}
        </TabsContent>
        <TabsContent value="members" className="mt-0" data-testid="owner-account-panel-members">
          <SitewideMembers />
        </TabsContent>
        <TabsContent value="plans" className="mt-0" data-testid="owner-account-panel-plans">
          <PlanManagementSection plansState={plans} embedded />
        </TabsContent>
        <TabsContent value="support" className="mt-0" data-testid="owner-account-panel-support">
          <SupportLog />
        </TabsContent>
        <TabsContent value="actions" className="mt-0" data-testid="owner-account-panel-actions">
          <SitewideActions />
        </TabsContent>
        <TabsContent value="dashboard-alert" className="mt-0" data-testid="owner-account-panel-dashboard-alert">
          <DashboardAlertAdmin />
        </TabsContent>
        <TabsContent value="texts" className="mt-0" data-testid="owner-account-panel-texts">
          <SmsSettingsAdmin />
        </TabsContent>
        <TabsContent value="user-types" className="mt-0" data-testid="owner-account-panel-user-types">
          <UserTypesAdmin />
        </TabsContent>
        <TabsContent value="square" className="mt-0" data-testid="owner-account-panel-square">
          <SquareAdmin />
        </TabsContent>
        <TabsContent value="mortise-locks" className="mt-0" data-testid="owner-account-panel-mortise-locks">
          <MortiseLocks />
        </TabsContent>
        <TabsContent value="billing" className="mt-0" data-testid="owner-account-panel-billing">
          <Billing />
        </TabsContent>
      </Tabs>
      <CompanyDialog company={data?.find((c) => c.id === openId) || null} onOpenChange={(o) => !o && setOpenId(null)} onSaved={reload} />
      <ConfirmDialog open={!!compTarget} onOpenChange={(o) => !o && setCompTarget(null)} onConfirm={() => assign(compTarget, "complimentary", true)}
        title={`Make ${compTarget?.name} complimentary?`} confirmLabel="Make complimentary"
        description="Their paid Stripe subscription will be cancelled straight away and they won't be charged again." />
      <ConfirmDialog open={!!target} onOpenChange={(o) => !o && setTarget(null)} onConfirm={act} testId="company-action-dialog"
        title={target?.suspended ? `Reactivate ${target?.name}?` : `Suspend ${target?.name}?`}
        description={target?.suspended ? "Their team gets access again straight away." : "Their managers and locksmiths lose access until reactivated. No data is deleted."}
        confirmLabel={target?.suspended ? "Reactivate" : "Suspend"} destructive={!target?.suspended} />
    </div>
  );
}
